import React, { useState, useEffect } from 'react';
import { Upload, Button, Card, message, Spin, Modal, Space, Empty } from 'antd';
import { UploadOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons';
import { ownerCardService } from '../../services/ownerCardService';
import './OwnerCardPage.css';

const OwnerCardPage = () => {
  const [ownerCard, setOwnerCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchOwnerCard();
  }, []);

  const fetchOwnerCard = async () => {
    setLoading(true);
    try {
      const response = await ownerCardService.getMyOwnerCard();
      if (response.data.success && response.data.ownerCard) {
        setOwnerCard(response.data.ownerCard);
      }
    } catch (error) {
      message.error('Kartvizit bilgileri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (info) => {
    const file = info.file.originFileObj || info.file;
    
    // Dosya kontrolü
    const isValid = file.type === 'application/pdf' || file.type.startsWith('image/');
    if (!isValid) {
      message.error('Sadece PDF veya resim dosyası yükleyebilirsiniz!');
      return;
    }
    
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Dosya 5MB\'dan küçük olmalıdır!');
      return;
    }

    setUploading(true);
    try {
      const response = await ownerCardService.uploadOwnerCard(file);
      if (response.data.success) {
        message.success('Kartvizit başarıyla yüklendi!');
        fetchOwnerCard();
      }
    } catch (error) {
      message.error('Yükleme başarısız oldu!');
    } finally {
      setUploading(false);
    }
  };

  const performDelete = async () => {
    setDeleting(true);
    try {
      const response = await ownerCardService.deleteMyOwnerCard();
      
      if (response.data.success) {
        message.success('Kartvizit başarıyla silindi!');
        setOwnerCard(null);
        setDeleteModalVisible(false);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Silme işlemi başarısız oldu!');
    } finally {
      setDeleting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    const kb = bytes / 1024;
    return kb > 1024 ? `${(kb / 1024).toFixed(2)} MB` : `${kb.toFixed(2)} KB`;
  };

  if (loading) {
    return (
      <div className="owner-card-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="owner-card-container">
      <Card className="owner-card-main">
        <h1 className="owner-card-title">
          <FileTextOutlined /> Kartvizit Yönetimi
        </h1>
        
        {ownerCard ? (
          <div className="owner-card-content">
            <div className="owner-card-info">
              <div className="info-item">
                <span className="info-label">Dosya Adı:</span>
                <span className="info-value">{ownerCard.originalFilename || 'Kartvizit'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Dosya Boyutu:</span>
                <span className="info-value">{formatFileSize(ownerCard.fileSize)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Yükleme Tarihi:</span>
                <span className="info-value">
                  {new Date(ownerCard.createdAt || ownerCard.uploadedAt).toLocaleDateString('tr-TR')}
                </span>
              </div>
            </div>

            <Space className="owner-card-actions">
              <Upload
                accept="image/*,.pdf"
                showUploadList={false}
                beforeUpload={() => false}
                onChange={handleUpload}
              >
                <Button 
                  type="primary" 
                  icon={<UploadOutlined />}
                  loading={uploading}
                >
                  Yeni Kartvizit Yükle
                </Button>
              </Upload>
              
              <Button 
                danger 
                icon={<DeleteOutlined />}
                onClick={() => setDeleteModalVisible(true)}
              >
                Sil
              </Button>
            </Space>
          </div>
        ) : (
          <div className="owner-card-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Henüz kartvizit yüklenmemiş"
            >
              <Upload
                accept="image/*,.pdf"
                showUploadList={false}
                beforeUpload={() => false}
                onChange={handleUpload}
              >
                <Button 
                  type="primary" 
                  icon={<UploadOutlined />}
                  loading={uploading}
                  size="large"
                >
                  Kartvizit Yükle
                </Button>
              </Upload>
            </Empty>
          </div>
        )}
      </Card>
      
      {/* Silme Modal */}
      <Modal
        title="Kartviziti Sil"
        open={deleteModalVisible}
        onOk={performDelete}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Sil"
        cancelText="İptal"
        okType="danger"
        confirmLoading={deleting}
      >
        <p>Kartviziti silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</p>
      </Modal>
    </div>
  );
};

export default OwnerCardPage;